import { FC, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { Select } from '@/features/vfo/uikit/Select';
import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { VfoDocumentsConflictsModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

import { ConflictResult, DocumentsConflictResolutionMap } from './types';

export const VfoDocumentsConflictsModal: FC<VfoDocumentsConflictsModalProps> = ({
  conflicts,
  onResolve,
}) => {
  const { t: tc } = useTranslation('common');
  const { t: tw } = useTranslation('widgets/modals', { keyPrefix: 'existingDocuments' });
  const modals = useModalsStore();

  const [results, setResults] = useState<DocumentsConflictResolutionMap>(
    new Map(conflicts.map((doc) => [doc.dtoId ?? '', ConflictResult.REPLACE])),
  );

  const items = [
    {
      label: tw('options.replace'),
      value: ConflictResult.REPLACE,
    },
    {
      label: tw('options.copy'),
      value: ConflictResult.COPY,
    },
    {
      label: tw('options.skip'),
      value: ConflictResult.SKIP,
    },
  ];

  const onSave = async () => {
    onResolve(results);
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title="These documents have already been imported">
      <ModalBody>
        <p>{tw('description')}</p>
        <div>
          {conflicts.map((doc) => (
            <Item key={doc.id}>
              <Text>{doc.title}</Text>
              <Select
                style={{ maxWidth: 250, width: '100%' }}
                options={items}
                defaultValue={ConflictResult.REPLACE}
                onChange={(value) => {
                  const newResults = new Map(results);
                  newResults.set(doc.dtoId ?? '', value);
                  setResults(newResults);
                }}
              />
            </Item>
          ))}
        </div>
      </ModalBody>
      <Buttons>
        <Button size="middle" onClick={onSave}>
          {tc('save')}
        </Button>
      </Buttons>
    </ModalContainerV1>
  );
};

const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Item = styled.div`
  padding-block: 12px;
  border-bottom: 1px solid ${COLORS.systemDark50A};
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
`;

const Text = styled.span`
  flex: 1;
`;

const Buttons = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: end;
  align-items: center;
`;
